import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import {
  AuthenticationService,
  AuthGuard,
  LGEDataService,
  LoginGuard,
  PendingGuard,
  PermissionsService,
  SignUpGuard,
} from 'src/commons';

import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  AlertModalComponent,
  ApproveRejectModalComponent,
  CharityChecktModalComponent,
  ConfirmationModalComponent,
  ConfirmModalComponent,
  CreditcardConfirmationModalComponent,
  LoaderModalComponent,
  LoginDetailComponent,
  ProcessExternalRequestModal,
} from 'src/commons/modals';
import { StoreModule } from '@ngrx/store';
import { storeFreeze } from 'ngrx-store-freeze';
import { reducers } from './state/app.selectors';
import {
  RouterStateSerializer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { AngularMaterialService } from 'src/commons/utilities/angular.material.service';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { HomeScreenComponent } from '../screens/home/home.component';
import { LoginScreenComponent } from '../screens/login/login.component';
import { CharitableContributionScreenComponent } from '../screens/charitable-contribution/charitable-contribution.component';
import { AppCommonModule } from './common.module';
import { AppRoutingModule, routes } from './app-routing.module';
import { NgxSpinnerModule } from 'ngx-spinner';
import { HttpClientModule } from '@angular/common/http';
import { EffectsModule } from '@ngrx/effects';
import { ApplicationEffects } from './state/app.effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { RouterModule } from '@angular/router';
import { LoginScreenModule } from '../screens/login/login.module';
import { CustomRouterStateSerializer } from 'src/commons/utilities';
import { AppStorageService } from 'src/commons/services/storage.service';
import { MaterialModule } from '../theme/material.module';
import { FormCCService } from 'src/commons/services/formCC.service';
import { PreviousRouteService } from 'src/commons/services/previousRoute.service';
import { CharityFormsScreenComponent } from '../screens/charity-forms/charity-forms.component';
import { CCMSTableComponent } from 'src/commons/components/ccms-table/ccms-table.component';
import { CCMSTableExtComponent } from 'src/commons/components/ccms-table-ext/ccms-table-ext.component';
import { MatchingGiftScreenComponent } from '../screens/matching-gift/matching-gift.component';
import { HomeEffects } from '../screens/home/state/home.effects';
import { AuthenticationEffects } from '../screens/login/state/login.effects';
import { CharitableContributionEffects } from '../screens/charitable-contribution/state/charitable-contribution.effects';
import { MatchingGiftEffects } from '../screens/matching-gift/state/matching-gift.effects';
import { HomeScreenModule } from '../screens/home/home.module';
import { CharityFormsScreenModule } from '../screens/charity-forms/charity-forms.module';
import { CharitableContributionScreenModule } from '../screens/charitable-contribution/charitable-contribution.module';
import { MatchingGiftScreenModule } from '../screens/matching-gift/matching-gift.module';
import { CorpService } from 'src/commons/services/corp.service';
import { CommunityGrantScreenComponent } from 'src/screens/community-grant/community-grant.component';
import { CommunityGrantScreenModule } from 'src/screens/community-grant/community-grant.module';
import { CommunityGrantEffects } from 'src/screens/community-grant/state/community-grant.effects';
import { SignaturePadModule } from 'angular2-signaturepad';
import { PlantPlanetScreenModule } from 'src/screens/plant-planet/plant-planet.module';
import { PlantPlanetEffects } from 'src/screens/plant-planet/state/plant-planet.effects';
import { PlantPlanetScreenComponent } from 'src/screens/plant-planet/plant-planet.component';
import { EventSponsorshipScreenComponent } from 'src/screens/event-sponsorship/event-sponsorship.component';
import { EventSponsorshipEffects } from 'src/screens/event-sponsorship/state/event-sponsorship.effects';
import { EventSponsorshipScreenModule } from 'src/screens/event-sponsorship/event-sponsorship.module';
import { AdminEffects } from 'src/screens/admin/state/admin.effects';
import { AdminScreenComponent } from 'src/screens/admin/admin.component';
import { AdminScreenModule } from 'src/screens/admin/admin.module';
import { PaymentConfigTableComponent } from 'src/commons/components/payment-config-table-component/payment-config-table-component';
import { PaymentConfigDetailsComponent } from 'src/commons/modals/payment-config-detail/payment-config-detail.component';
import { PaymentConfigScreenComponent } from 'src/screens/payment-config/payment-config.component';
import { PaymentConfigScreenModule } from 'src/screens/payment-config/payment-config.module';
import { PaymentConfigEffects } from 'src/screens/payment-config/state/payment-config.effects';
import { OverrideUserDetailsComponent } from 'src/commons/modals/override-user-details/overried-user-details.component';
import { OverrideReportingScreenComponent } from 'src/screens/override-reporting/override-reporting.component';
import { OverrideReportingScreenModule } from 'src/screens/override-reporting/override-reporting.module';
import { OverrideReportingEffects } from 'src/screens/override-reporting/state/override-reporting.effects';
import { PaymentCheckTableComponent } from 'src/commons/components/payment-check-table-component/payment-check-table-component';
import { PaymentCheckExtTableComponent } from 'src/commons/components/payment-check-ext-table-component/payment-check-ext-table-component';
import { PaymentCheckScreenComponent } from 'src/screens/payment-check/payment-check.component';
import { PaymentCheckEffects } from 'src/screens/payment-check/state/payment-check.effects';
import { PaymentCheckScreenModule } from 'src/screens/payment-check/payment-check.module';
import { PaymentCreditScreenComponent } from 'src/screens/payment-credit/payment-credit.component';
import { PaymentCreditScreenModule } from 'src/screens/payment-credit/payment-credit.module';
import { PaymentCreditEffects } from 'src/screens/payment-credit/state/payment-credit.effects';
import { PaymentCreditTableComponent } from 'src/commons/components/payment-credit-table-component/payment-credit-table-component';
import { PaymentCreditExtTableComponent } from 'src/commons/components/payment-credit-ext-table-component/payment-credit--ext-table-component';
import { PaymentFoundationTrusteeScreenComponent } from 'src/screens/payment-foundation-trustee/payment-foundation-trustee.component';
import { PaymentFoundationTrusteeScreenModule } from 'src/screens/payment-foundation-trustee/payment-foundation-trustee.module';
import { PaymentFoundationTrusteeEffects } from 'src/screens/payment-foundation-trustee/state/payment-foundation-trustee.effects';
import { ProfileScreenModule } from 'src/screens/profile/profile.module';
import { ProfileScreenComponent } from 'src/screens/profile/profile.component';
import { ProfileEffects } from 'src/screens/profile/state/profile.effects';
import { EinConfigurationScreenModule } from 'src/screens/ein-configuration/ein-configuration.module';
import { EinConfigurationEffects } from 'src/screens/ein-configuration/state/ein-configuration.effects';
import { EinConfigurationScreenComponent } from 'src/screens/ein-configuration/ein-configuration.component';
import { EINConfigTableComponent } from 'src/commons/components/ein-config-table-component/ein-config-table.component';
import { EinOrgDetailsComponent } from 'src/commons/modals/ein-org-details/ein-org-details.component';
import { FormConfigurationScreenModule } from 'src/screens/form-configuration/form-configuration.module';
import { FormConfigurationEffects } from 'src/screens/form-configuration/state/form-configuration.effects';
import { FormConfigurationScreenComponent } from 'src/screens/form-configuration/form-configuration.component';
import { FoundationPaymentTableComponent } from 'src/commons/components/foundation-payment-table-component/foundation-payment-table.component';
import { AlmConfigurationScreenComponent } from 'src/screens/alm-configuration/alm-configuration.component';
import { AlmConfigurationScreenModule } from 'src/screens/alm-configuration/alm-configuration.module';
import { ExternalUsersManagementScreenModule } from 'src/screens/external-users-management/external-users-management.module';
import { ExternalUsersManagementEffects } from 'src/screens/external-users-management/state/external-users-management.effects';
import { ExternalUsersManagementScreenComponent } from 'src/screens/external-users-management/external-users-management.component';
import { ExternalAccessRequestComponent } from 'src/commons/modals/external-access-details/external-access-details.component';
import { ExtManagementTableComponent } from 'src/commons/components/ext-user-table-component/ext-management-table.component';
import { FormStatusScreenModule } from 'src/screens/form-status/form-status.module';
import { FormStatusEffects } from 'src/screens/form-status/state/form-status.effects';
import { FormStatusScreenComponent } from 'src/screens/form-status/form-status.component';
import { FormStatusTableTableComponent } from 'src/commons/components/form-status-table-component/form-status-table-component';
import { ComposeEmailModalComponent } from 'src/commons/modals/compose-email/compose-email.component';
import { EmailTemplateModalComponent } from 'src/commons/modals/email-template/email-template.component';
import { EmailConfigurationScreenModule } from 'src/screens/email-configuration/email-configuration.module';
import { EmailConfigurationEffects } from 'src/screens/email-configuration/state/email-configuration.effects';
import { EmailConfigurationScreenComponent } from 'src/screens/email-configuration/email-configuration.component';
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';
import { AccDistDetailsComponent } from 'src/commons/components/acc-dist-details/acc-dist-details.component';
import { ProjectTaskDetailsComponent } from 'src/commons/components/project-task-details/project-task-details.component';
import { AccountingDistributionExternalScreenComponent } from 'src/screens/accounting-distribution-external/accounting-distribution-external.component';
import { AccountingDistributionExternalScreenModule } from 'src/screens/accounting-distribution-external/accounting-distribution-external.module';
import { AccountExtScreenModule } from 'src/screens/accounting-distribution-external/account/account.module';
import { AccountExtScreenComponent } from 'src/screens/accounting-distribution-external/account/account.component';
import { ProjectTaskExtScreenComponent } from 'src/screens/accounting-distribution-external/project-task/project-task.component';
import { ProjectTaskExtScreenModule } from 'src/screens/accounting-distribution-external/project-task/project-task.module';
import { OrganizationExtScreenModule } from 'src/screens/accounting-distribution-external/organization/organization.module';
import { OrganizationExtScreenComponent } from 'src/screens/accounting-distribution-external/organization/organization.component';
import { OebsConfigExtScreenComponent } from 'src/screens/accounting-distribution-external/oebs-config/oebs-config.component';
import { OebsConfigExtScreenModule } from 'src/screens/accounting-distribution-external/oebs-config/oebs-config.module';
import { ExpTypeExtScreenModule } from 'src/screens/accounting-distribution-external/exp-type/exp-type.module';
import { ExpTypeExtScreenComponent } from 'src/screens/accounting-distribution-external/exp-type/exp-type.component';
import { ExpOrgExtScreenComponent } from 'src/screens/accounting-distribution-external/exp-org/exp-org.component';
import { ExpOrgExtScreenModule } from 'src/screens/accounting-distribution-external/exp-org/exp-org.module';
import { CompanyExtScreenModule } from 'src/screens/accounting-distribution-external/company/company.module';
import { CompanyExtScreenComponent } from 'src/screens/accounting-distribution-external/company/company.component';
import { SetPasswordComponent } from 'src/screens/set-password/set-password.component';
import { ForgotPasswordComponent } from 'src/screens/forgot-password/forgot-password.component';
import { AccDistTableComponent } from 'src/commons/components/accDist-table-component/accDist-table-component';
import { TextMaskModule } from 'angular2-text-mask';
import { MatStepperModule } from '@angular/material/stepper';
import { LandingScreenComponent } from 'src/commons/components/landing-screen/landing-screen';

@NgModule({
  imports: [
    AppRoutingModule,
    AppCommonModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserModule,
    CommonModule,
    BrowserModule,
    BrowserAnimationsModule,
    MaterialModule,
    FlexLayoutModule,
    MatStepperModule,
    NgxSpinnerModule,
    HttpClientModule,
    SignaturePadModule,
    QuillModule,
    ToastrModule.forRoot({
      positionClass: 'toast-center-center',
      countDuplicates: true,
      timeOut: 3000,
    }),
    TextMaskModule,
    StoreModule.forRoot(reducers, { metaReducers: [storeFreeze] }),
    StoreRouterConnectingModule.forRoot(),
    LoginScreenModule.forRoot(),
    ProfileScreenModule,
    HomeScreenModule,
    CharityFormsScreenModule,
    CharitableContributionScreenModule,
    MatchingGiftScreenModule,
    CommunityGrantScreenModule,
    PlantPlanetScreenModule,
    EventSponsorshipScreenModule,
    AdminScreenModule,
    EinConfigurationScreenModule,
    EmailConfigurationScreenModule,
    FormConfigurationScreenModule,
    AlmConfigurationScreenModule,
    PaymentConfigScreenModule,
    OverrideReportingScreenModule,
    PaymentCheckScreenModule,
    PaymentCreditScreenModule,
    PaymentFoundationTrusteeScreenModule,
    ExternalUsersManagementScreenModule,
    FormStatusScreenModule,
    AccountingDistributionExternalScreenModule,
    AccountExtScreenModule,
    CompanyExtScreenModule,
    ExpOrgExtScreenModule,
    ExpTypeExtScreenModule,
    OebsConfigExtScreenModule,
    OrganizationExtScreenModule,
    ProjectTaskExtScreenModule,
    StoreDevtoolsModule.instrument({}),
    EffectsModule.forRoot([
      ApplicationEffects,
      ProfileEffects,
      HomeEffects,
      AuthenticationEffects,
      CharitableContributionEffects,
      MatchingGiftEffects,
      CommunityGrantEffects,
      PlantPlanetEffects,
      EventSponsorshipEffects,
      AdminEffects,
      EinConfigurationEffects,
      FormConfigurationEffects,
      PaymentConfigEffects,
      OverrideReportingEffects,
      PaymentCheckEffects,
      PaymentCreditEffects,
      PaymentFoundationTrusteeEffects,
      ExternalUsersManagementEffects,
      FormStatusEffects,
      EmailConfigurationEffects,
    ]),
    RouterModule.forRoot(routes, {
      useHash: true,
      scrollPositionRestoration: 'top',
      onSameUrlNavigation: 'reload',
    }),
  ],
  declarations: [
    AppComponent,
    HomeScreenComponent,
    LoginScreenComponent,
    SetPasswordComponent,
    ForgotPasswordComponent,
    ProfileScreenComponent,
    CharityFormsScreenComponent,
    CharitableContributionScreenComponent,
    CommunityGrantScreenComponent,
    PlantPlanetScreenComponent,
    EventSponsorshipScreenComponent,
    MatchingGiftScreenComponent,
    AdminScreenComponent,
    AlmConfigurationScreenComponent,
    FormConfigurationScreenComponent,
    EinConfigurationScreenComponent,
    PaymentConfigScreenComponent,
    PaymentCheckScreenComponent,
    PaymentCreditScreenComponent,
    PaymentFoundationTrusteeScreenComponent,
    ExternalUsersManagementScreenComponent,
    FormStatusScreenComponent,
    AccountingDistributionExternalScreenComponent,
    AccountExtScreenComponent,
    CompanyExtScreenComponent,
    ExpOrgExtScreenComponent,
    ExpTypeExtScreenComponent,
    OebsConfigExtScreenComponent,
    OrganizationExtScreenComponent,
    ProjectTaskExtScreenComponent,
    EmailConfigurationScreenComponent,
    OverrideReportingScreenComponent,
    CharityChecktModalComponent,
    ApproveRejectModalComponent,
    ConfirmModalComponent,
    AlertModalComponent,
    LoaderModalComponent,
    ConfirmationModalComponent,
    CharityChecktModalComponent,
    ProcessExternalRequestModal,
    LoginDetailComponent,
    CCMSTableComponent,
    CCMSTableExtComponent,
    PaymentConfigTableComponent,
    PaymentConfigDetailsComponent,
    OverrideUserDetailsComponent,
    PaymentCheckTableComponent,
    PaymentCheckExtTableComponent,
    PaymentCreditTableComponent,
    PaymentCreditExtTableComponent,
    CreditcardConfirmationModalComponent,
    FoundationPaymentTableComponent,
    EINConfigTableComponent,
    EinOrgDetailsComponent,
    ExternalAccessRequestComponent,
    ExtManagementTableComponent,
    FormStatusTableTableComponent,
    ComposeEmailModalComponent,
    EmailTemplateModalComponent,
    AccDistDetailsComponent,
    AccDistTableComponent,
    ProjectTaskDetailsComponent,
    LandingScreenComponent,
  ],
  entryComponents: [
    ApproveRejectModalComponent,
    ConfirmModalComponent,
    AlertModalComponent,
    LoaderModalComponent,
    ConfirmationModalComponent,
    CharityChecktModalComponent,
    ProcessExternalRequestModal,
    LoginDetailComponent,
    PaymentConfigTableComponent,
    PaymentConfigDetailsComponent,
    OverrideUserDetailsComponent,
    PaymentCheckTableComponent,
    PaymentCheckExtTableComponent,
    PaymentCreditTableComponent,
    PaymentCreditExtTableComponent,
    CreditcardConfirmationModalComponent,
    FoundationPaymentTableComponent,
    EINConfigTableComponent,
    EinOrgDetailsComponent,
    ExternalAccessRequestComponent,
    ExtManagementTableComponent,
    FormStatusTableTableComponent,
    ComposeEmailModalComponent,
    EmailTemplateModalComponent,
    AccDistDetailsComponent,
    AccDistTableComponent,
    ProjectTaskDetailsComponent,
    LandingScreenComponent,
  ],
  providers: [
    AngularMaterialService,
    LGEDataService,
    PermissionsService,
    AppStorageService,
    FormCCService,
    PreviousRouteService,
    AuthenticationService,
    CorpService,
    AuthGuard,
    LoginGuard,
    SignUpGuard,
    PendingGuard,
    { provide: RouterStateSerializer, useClass: CustomRouterStateSerializer },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
